import React, { useCallback, useMemo } from 'react';

import {
  LOGO_VERSION,
  BLACK_LOGO,
  WHITE_LOGO,
  V3_LOGO,
} from '@shared/config/constants/modules/logo';

import {
  isVariationV3Route,
  isVariationV4Route,
  isVariationV5Route,
  isWhiteTheme,
} from '@shared/utils';

import { ILogo, ILogoConfig } from '@shared/types/common-interfaces';
import { LANDING_PAGE_VIEWS } from '@shared/config/constants/app';
import { isLY } from '@entities/country';
import { SeparatorV2 } from '@shared/assets/svgs/SeparatorV2';
import { useAtomValue } from 'jotai';
import { dcbAtom, globalAtom } from '@shared/store/slice-atoms';
import { Separator } from '@shared/assets/svgs/Separator';
import { Hungama } from '@shared/assets/svgs/Hungama';
import { hungamaCointriesList } from '@shared/ui/logo/model/utils';
import { useThemeContext } from '@shared/hooks';
import { useCheckMarkLYCondition } from '@features/phone-subscription/model/hooks/useCheckMarkLYCondition';

import '@shared/ui/logo/styles/LogoStyles.css';

interface Props {
  isMin: boolean;
  logoConfig: ILogoConfig;
  uniqueApiSource: string | boolean;
  renderLogo: boolean;
}

const Logo: React.FC<Props> = ({ isMin, logoConfig, uniqueApiSource, renderLogo }) => {
  const { theme } = useThemeContext();
  const isWhite = isWhiteTheme(theme);

  const dcb = useAtomValue(dcbAtom);
  const { landingPageView } = useAtomValue(globalAtom);
  const homeData = dcb.homeData;
  const operator = dcb.operator;
  const isV3Route = isVariationV3Route(dcb.variationKey);
  const isV4Route = isVariationV4Route(dcb.variationKey);
  const isV5Route = isVariationV5Route(dcb.variationKey);

  const getLogoConfig = useCallback((): ILogo => {
    if (isV3Route) {
      return V3_LOGO;
    }

    return isWhite && !isMin ? BLACK_LOGO : WHITE_LOGO;
  }, [isWhite, isMin, isV3Route]);

  const _logoConfig: ILogoConfig = logoConfig?.version ? logoConfig : getLogoConfig();

  const { isCheckMarkLY } = useCheckMarkLYCondition();
  const isLeftPosition: boolean = logoConfig.position === 'left' && !isCheckMarkLY;

  const isEbtekarCondition =
    homeData.source === 'api-web-LY_weekly_05022025' && isLY(homeData.country);

  const imgClassName = useMemo<string>(() => {
    let className = '';

    if (isV3Route || isV4Route) {
      className = 'logo-sizes-v3 ';
    } else if (isLeftPosition || uniqueApiSource) {
      className = 'logo-size-old ';
    }

    if (isV5Route) {
      className = '';
    }

    if (!isLeftPosition && !uniqueApiSource) {
      className += 'img-fluid logo';
    }

    if (isEbtekarCondition || isCheckMarkLY) {
      className += ' cptpl_logo';
    }

    return className;
  }, [isLeftPosition, isV3Route, isV4Route, uniqueApiSource, isEbtekarCondition, isCheckMarkLY]);

  const wrapperClassName = useMemo<string>(() => {
    if (isV3Route) {
      return 'logo-flex-bottom';
    }
    if (isCheckMarkLY || (!isLeftPosition && !uniqueApiSource)) {
      return `text-center logo-flex-center`;
    }

    return '';
  }, [isLeftPosition, uniqueApiSource, isV3Route, isCheckMarkLY]);

  const OperatorLogo = useMemo<React.ReactNode>(() => {
    if (!renderLogo) {
      return null;
    }

    if (isV3Route && operator?.logo && landingPageView === LANDING_PAGE_VIEWS.PHONE_NUMBER) {
      return (
        <>
          <Separator />
          <img src={operator.logo} alt="operator" height="32" />
        </>
      );
    }

    if (isV3Route && hungamaCointriesList[homeData?.country?.toUpperCase()]) {
      return (
        <>
          <div className="logo-margin">
            <SeparatorV2 />
          </div>

          <Hungama />
        </>
      );
    }

    return null;
  }, [isV3Route, landingPageView, operator, renderLogo, homeData?.country]);

  const SecondaryLogo = useMemo<React.ReactNode>(() => {
    if (!_logoConfig.secondaryLogo || isV3Route || !renderLogo) {
      return null;
    }

    return (
      <img
        alt="secondary logo"
        src={_logoConfig.secondaryLogo}
        className="img-fluid logo secondary-logo"
      />
    );
  }, [_logoConfig.secondaryLogo, isV3Route, renderLogo]);

  return (
    <div className={wrapperClassName}>
      {isMin ? (
        <img src={_logoConfig.version} className="img-fluid logo" alt="logo" />
      ) : (
        <img
          src={!uniqueApiSource ? _logoConfig.version : LOGO_VERSION.black}
          className={imgClassName}
          alt="logo"
        />
      )}

      {OperatorLogo}

      {SecondaryLogo}
    </div>
  );
};

export default Logo;
